import React, { useEffect, useState } from 'react';
import api from '../../api/axiosConfig';

interface Device {
  _id?: string;
  modelCode: string;
  name_vi: string;
  name_en: string;
  warrantyPeriodInMonths: number;
}

const QLThietBi = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // --- Lấy token ---
  const getToken = () => JSON.parse(sessionStorage.getItem('user') || '{}')?.accessToken;

  // --- Gọi API lấy danh sách ---
  const fetchDevices = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const { data } = await api.get('/devices', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDevices(data);
    } catch (err) {
      console.error('❌ Lỗi tải danh sách thiết bị:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  // --- Xử lý chọn hoặc hủy chọn thiết bị ---
  const handleSelectDevice = (device: Device) => setSelectedDevice(device);
  const handleCancel = () => setSelectedDevice(null);

  // --- Thêm mới hoặc cập nhật thiết bị ---
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDevice) return;
    try {
      const token = getToken();
      if (selectedDevice._id) {
        await api.put(`/devices/${selectedDevice._id}`, selectedDevice, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('✅ Cập nhật thiết bị thành công!');
      } else {
        await api.post('/devices', selectedDevice, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('✅ Thêm thiết bị mới thành công!');
      }
      setSelectedDevice(null);
      fetchDevices();
    } catch (err) {
      console.error('❌ Lỗi lưu thiết bị:', err);
      alert('Không thể lưu thiết bị. Vui lòng thử lại.');
    }
  };

  // --- Xóa thiết bị ---
  const handleDelete = async (id: string) => {
    if (!window.confirm('Bạn có chắc muốn xóa thiết bị này?')) return;
    try {
      const token = getToken();
      await api.delete(`/devices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('✅ Đã xóa thiết bị!');
      fetchDevices();
    } catch (err) {
      console.error('❌ Lỗi khi xóa thiết bị:', err);
      alert('Không thể xóa thiết bị.');
    }
  };

  // --- Hàm xử lý nhập liệu ---
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDevice) return;
    const { name, value } = e.target;
    setSelectedDevice({ ...selectedDevice, [name]: value });
  };

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Quản lý Thiết bị</h1>

      {/* Nút trở về Dashboard */}
      <div className="mb-6">
        <a
          href="/admin/dashboard"
          className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
        >
          ← Trở về Dashboard
        </a>
      </div>

      {/* Form thêm / sửa thiết bị */}
      {selectedDevice ? (
        <form
          onSubmit={handleSave}
          className="mb-8 bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4"
        >
          <h2 className="text-xl font-bold text-gray-800 border-b pb-2">
            {selectedDevice._id ? 'Chỉnh sửa thiết bị' : 'Thêm thiết bị mới'}
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mã Model
            </label>
            <input
              type="text"
              name="modelCode"
              required
              value={selectedDevice.modelCode}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên Thiết bị (VI)
            </label>
            <input
              type="text"
              name="name_vi"
              required
              value={selectedDevice.name_vi}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên Thiết bị (EN)
            </label>
            <input
              type="text"
              name="name_en"
              required
              value={selectedDevice.name_en}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Thời hạn bảo hành (tháng)
            </label>
            <input
              type="number"
              name="warrantyPeriodInMonths"
              required
              value={selectedDevice.warrantyPeriodInMonths}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="px-5 py-2 bg-green-700 text-white font-semibold rounded-md hover:bg-green-800 transition"
            >
              {selectedDevice._id ? 'Cập nhật' : 'Lưu mới'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-5 py-2 bg-gray-200 text-gray-800 rounded-md font-semibold hover:bg-gray-300 transition"
            >
              Hủy
            </button>
          </div>
        </form>
      ) : (
        <div className="mb-6">
          <button
            onClick={() =>
              setSelectedDevice({
                modelCode: '',
                name_vi: '',
                name_en: '',
                warrantyPeriodInMonths: 12,
              })
            }
            className="px-5 py-2 bg-green-700 text-white rounded-md font-semibold hover:bg-green-800 transition"
          >
            + Thêm thiết bị mới
          </button>
        </div>
      )}

      {/* Danh sách thiết bị */}
      {isLoading ? (
        <p className="text-center text-gray-500 py-6 font-medium">
          Đang tải danh sách thiết bị...
        </p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-green-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wide">
                  Mã Model
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wide">
                  Tên Thiết bị
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wide">
                  Bảo hành (tháng)
                </th>
                <th className="px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {devices.length > 0 ? (
                devices.map((device, idx) => (
                  <tr key={device._id || idx} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-3 font-mono text-sm text-gray-800">
                      {device.modelCode}
                    </td>
                    <td className="px-6 py-3 text-gray-800">{device.name_vi}</td>
                    <td className="px-6 py-3 text-gray-800">
                      {device.warrantyPeriodInMonths}
                    </td>
                    <td className="px-6 py-3 text-center">
                      <button
                        onClick={() => handleSelectDevice(device)}
                        className="px-3 py-1 text-sm text-blue-600 font-semibold hover:text-blue-800"
                      >
                        Sửa
                      </button>
                      <button
                        onClick={() => handleDelete(device._id!)}
                        className="px-3 py-1 text-sm text-red-600 font-semibold hover:text-red-800"
                      >
                        Xóa
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-gray-500">
                    Không có thiết bị nào
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default QLThietBi;
